import React, { useEffect, useRef } from "react"
import { socket } from "@/utils/utils"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  removeToastNotification,
  addToastNotification,
} from "@/store/globalSlice"
import { runTradeMinimizeAnimation } from "@/utils/animationHelpers"
import { TradePanelSmall } from "@/Components/TradePanelSmall"
import { SwipeableToast } from "@/Components/SwipeableToast"
import { TagCostEstimationResourceCollectionFilter } from "aws-sdk/clients/devopsguru"

type ToastType = IToastNotification["type"]

const toastTypeToDuration: Record<ToastType, number> = {
  "game-invite": 500000,
  "trade-declined": 4000,
  "trade-completed": 6000,
  // Longer than a trade decline: it changes who you are playing against for the
  // rest of the game, and the log entry is the only other place it shows up.
  "player-replaced-by-bot": 6000,
  // The longest-lived toast by a distance. It lands mid-turn, so it has to
  // survive somebody being deep in a roll, a trade or a build before they even
  // look up — and once they do, it is telling them to change how they are
  // playing, not just what happened. It still takes itself away in the end.
  "endgame-warning": 15000,
  // How a fast-pace vote went. Longer than a trade result: an accepted vote
  // changes every clock in the game from this moment on, and it can land on
  // somebody who answered thirty seconds ago and has been playing since.
  "fast-pace-result": 8000,
  general: 3000,
}

// Dismiss lives in a gutter down the LEFT edge of every toast, just inside the
// accent rail — the stack sits on top of the game's top-right HUD, so a close
// button on the right lands within a few pixels of Leave. A full-height column
// rather than a chip in the content row: it keeps its own lane, so the card's
// layout is unchanged apart from the left padding that makes room for it, and
// the whole strip is the click target.
//
// On touch there is nothing in the lane at all: a target that small next to the
// HUD is a mis-tap waiting to happen, so the card is swiped away instead (see
// SwipeableToast) and the gutter is left empty — cards that size themselves for
// mobile reclaim it as padding.
const ToastDismissButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const isMobileDevice = useAppSelector((state) => state.global.isMobile)
  if (isMobileDevice) return null
  return (
    <button
      onClick={onClick}
      aria-label="Dismiss notification"
      className="absolute inset-y-0 left-2 z-10 w-10 flex items-center justify-center border-r border-white/10 text-white/40 hover:text-white hover:bg-white/10 transition-colors duration-200"
    >
      <i className="fa-solid fa-xmark text-xl" />
    </button>
  )
}

const ToastContainer: React.FC = () => {
  const dispatch = useAppDispatch()
  const toastNotifications = useAppSelector(
    (state) => state.global.toastNotifications
  )
  const isMobileDevice = useAppSelector((state) => state.global.isMobile)
  const navigate = useNavigate()

  // Toasts currently flying into the trades panel — guards against the timer
  // effect (which resets whenever the toast list changes) starting a second
  // flight for the same toast.
  const minimizingToastsRef = useRef<Set<string>>(new Set())

  // "Successful trade" toasts don't just vanish: they minimize into their
  // completed row of the trades panel so players learn the details stay
  // reviewable there.
  const minimizeTradeCompletedToast = (
    toast: ITradeCompletedToastNotification
  ) => {
    if (minimizingToastsRef.current.has(toast.id)) return
    minimizingToastsRef.current.add(toast.id)
    const el = document.getElementById(`trade-toast-${toast.id}`)
    if (!el) {
      dispatch(removeToastNotification(toast.id))
      return
    }
    runTradeMinimizeAnimation(
      el,
      () => {
        dispatch(removeToastNotification(toast.id))
        minimizingToastsRef.current.delete(toast.id)
      },
      toast.tradeId
    )
  }

  useEffect(() => {
    // Auto-remove toasts after 3 seconds (except game invites which have longer duration)
    const timers = toastNotifications.map((toast) => {
      const duration = toastTypeToDuration[toast.type] || 3000
      return setTimeout(() => {
        if (toast.type === "trade-completed") {
          minimizeTradeCompletedToast(toast)
        } else {
          dispatch(removeToastNotification(toast.id))
        }
      }, duration)
    })

    return () => timers.forEach(clearTimeout)
  }, [toastNotifications, dispatch])

  // Mirrors the accent-rail treatment the other toasts use (trade-declined,
  // endgame-warning, …) so a plain "Profile saved" style toast doesn't look
  // like it wandered in from a different, older version of the app.
  const getToastAccent = (messageType: "success" | "error" | "info") => {
    switch (messageType) {
      case "success":
        return {
          border: "border-emerald-400/50",
          background: "from-[#0e2a1a] via-[#0a1f14] to-black",
          rail: "from-emerald-400 to-emerald-600",
          ring: "ring-emerald-500/50",
          text: "text-emerald-300",
          icon: "fa-circle-check",
        }
      case "error":
        return {
          border: "border-red-500/60",
          background: "from-[#4a1414] via-[#2a0e0e] to-black",
          rail: "from-red-400 to-red-600",
          ring: "ring-red-500/60",
          text: "text-red-300",
          icon: "fa-circle-exclamation",
        }
      case "info":
        return {
          border: "border-sky-500/60",
          background: "from-[#12283f] via-[#0d1a29] to-black",
          rail: "from-sky-400 to-sky-600",
          ring: "ring-sky-500/60",
          text: "text-sky-300",
          icon: "fa-circle-info",
        }
      default:
        return {
          border: "border-white/15",
          background: "from-[#2a2a2a] via-[#1a1a1a] to-black",
          rail: "from-gray-400 to-gray-600",
          ring: "ring-white/30",
          text: "text-white/80",
          icon: "fa-bell",
        }
    }
  }

  const handleAcceptInvite = (gameId: string, toastId: string) => {
    // Hide the toast immediately when accepting
    dispatch(removeToastNotification(toastId))

    socket.emit(
      "joinRoom",
      { roomid: gameId, throughInvite: true },
      (response) => {
        if (response) {
          navigate(`/newgame/${gameId}`)
        } else {
          dispatch(
            addToastNotification({
              type: "general",
              message: "Game is full",
              messageType: "error",
            })
          )
        }
      }
    )
  }

  const renderGeneralNotification = (toast: IGeneralToastNotification) => {
    const accent = getToastAccent(toast.messageType)
    return (
      <div
        key={toast.id}
        className={`toast-pop relative flex items-center gap-4 pl-16 pr-6 py-5 rounded-3xl overflow-hidden font-poppins text-white border-2 min-w-[360px] max-w-[500px] ${accent.border} bg-gradient-to-br ${accent.background} shadow-[0_18px_45px_rgba(0,0,0,0.55)]`}
      >
        <div
          className={`absolute inset-y-0 left-0 w-2 bg-gradient-to-b ${accent.rail}`}
        />
        <ToastDismissButton
          onClick={() => dispatch(removeToastNotification(toast.id))}
        />
        <div
          className={`relative shrink-0 flex items-center justify-center h-11 w-11 rounded-full bg-black/40 ring-2 ${accent.ring}`}
        >
          <i className={`fa-solid ${accent.icon} ${accent.text} text-lg`} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-poppins text-base font-semibold leading-tight text-white">
            {toast.message}
          </span>
        </div>
      </div>
    )
  }

  const renderTradeDeclinedNotification = (
    toast: ITradeDeclinedToastNotification
  ) => (
    <div
      key={toast.id}
      data-testid="notification.trade-declined"
      className="trade-declined-toast relative flex items-center gap-5 pl-16 pr-6 py-6 rounded-3xl w-[clamp(440px,32vw,620px)] overflow-hidden font-poppins text-white border-2 border-red-500/60 bg-gradient-to-br from-[#4a1414] via-[#2a0e0e] to-black"
    >
      {/* Red accent rail */}
      <div className="absolute inset-y-0 left-0 w-2 bg-gradient-to-b from-red-400 to-red-600" />

      <ToastDismissButton
        onClick={() => dispatch(removeToastNotification(toast.id))}
      />

      {/* Player piece avatar with a "declined" badge */}
      <div className="relative shrink-0">
        <div className="flex items-center justify-center h-20 w-20 rounded-full bg-black/50 ring-4 ring-red-500/60 shadow-inner">
          {toast.pieceImgSrc ? (
            <img className="h-14 w-14 object-contain" src={toast.pieceImgSrc} />
          ) : (
            <i className="fa-solid fa-user text-white/50 text-3xl" />
          )}
        </div>
        <span className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-red-500 ring-4 ring-[#2a0e0e] flex items-center justify-center shadow-md">
          <i className="fa-solid fa-xmark text-white text-base" />
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-red-400">
          <i className="fa-solid fa-handshake-slash" />
          Trade declined
        </div>
        <div className="text-2xl font-bold leading-tight mt-1">
          <span className="text-red-300">{toast.username}</span>
          <span className="text-white"> declined your trade</span>
        </div>
      </div>
    </div>
  )

  const renderTradeCompletedNotification = (
    toast: ITradeCompletedToastNotification
  ) => {
    // Mirror the center notification's framing: each column is what that player
    // walked away with — offerer on the left, recipient on the right — so the
    // properties cross over and the net cash flips sign between the two.
    const netMoney = toast.moneyTaken - toast.moneyGiven
    return (
      <div
        key={toast.id}
        id={`trade-toast-${toast.id}`}
        data-testid="notification.trade-accepted"
        className="toast-pop relative flex flex-col gap-2 px-5 py-4 rounded-2xl w-[clamp(400px,30vw,500px)] overflow-hidden font-poppins text-white border border-emerald-400/40 bg-gradient-to-br from-[#0e2a1a] via-[#0a1f14] to-black shadow-[0_18px_45px_rgba(0,0,0,0.6),0_0_24px_rgba(16,185,129,0.25)]"
      >
        <div className="flex items-center gap-2">
          {!isMobileDevice && (
            <button
              onClick={() => minimizeTradeCompletedToast(toast)}
              aria-label="Dismiss notification"
              className="shrink-0 -ml-1 w-7 h-7 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition-colors duration-200 flex items-center justify-center"
            >
              <i className="fa-solid fa-xmark text-base" />
            </button>
          )}
          <i className="fa-solid fa-circle-check text-emerald-400" />
          <span className="text-sm font-bold uppercase tracking-[0.15em] text-emerald-300">
            Successful trade
          </span>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
          <TradePanelSmall
            playerOrder={toast.offeringPlayerOrder}
            receivedProperties={toast.takenProperties}
            money={netMoney}
          />
          <div className="flex flex-col items-center justify-center gap-2 text-white/40 px-1">
            <i className="fa-solid fa-arrow-right" />
            <i className="fa-solid fa-arrow-left" />
          </div>
          <TradePanelSmall
            playerOrder={toast.offeredPlayerOrder}
            receivedProperties={toast.givenProperties}
            money={-netMoney}
          />
        </div>
      </div>
    )
  }

  const renderGameInviteNotification = (toast: IGameInviteNotification) => (
    <div
      key={toast.id}
      className="relative flex items-start gap-5 pl-16 pr-6 py-5 rounded-2xl border-2 shadow-2xl overflow-hidden backdrop-blur-sm transition-all duration-300 min-w-[480px] max-w-[580px] bg-brown-700-light-2 border-brown-500/40 text-white"
    >
      <ToastDismissButton
        onClick={() => dispatch(removeToastNotification(toast.id))}
      />
      <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
        <span className="text-3xl">🎮</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-fourth text-xl font-bold mb-2 text-yellow-100">
          Game Invitation
        </div>
        <div className="text-base text-brown-100 mb-3 leading-relaxed">
          <span className="font-semibold text-yellow-200">
            {toast.invitedBy}
          </span>{" "}
          invited you to join their game
        </div>
        <div className="text-base text-brown-100 mb-5 leading-relaxed">
          <span className="font-semibold text-yellow-200">
            {toast.currentPlayers}/{toast.maxPlayers}
          </span>{" "}
          players
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleAcceptInvite(toast.gameId, toast.id)}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl text-sm font-bold transition-all duration-200 shadow-lg hover:shadow-xl border border-green-400/60 hover:scale-105 active:scale-95"
          >
            Accept Invite
          </button>
          <button
            onClick={() => dispatch(removeToastNotification(toast.id))}
            className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl text-sm font-bold transition-all duration-200 shadow-lg hover:shadow-xl border border-gray-400/60 hover:scale-105 active:scale-95"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  )

  // Informational, not a failure — slate/sky rather than the red of a decline.
  // Same shape as the trade-declined toast so the piece avatar tells you which
  // seat changed hands before you have finished reading the words.
  const renderPlayerReplacedByBotNotification = (
    toast: IPlayerReplacedByBotToastNotification
  ) => (
    <div
      key={toast.id}
      data-testid="notification.player-replaced-by-bot"
      className="toast-pop relative flex items-center gap-5 pl-16 pr-6 py-6 rounded-3xl w-[clamp(440px,32vw,620px)] overflow-hidden font-poppins text-white border-2 border-sky-500/60 bg-gradient-to-br from-[#12283f] via-[#0d1a29] to-black"
    >
      {/* Sky accent rail */}
      <div className="absolute inset-y-0 left-0 w-2 bg-gradient-to-b from-sky-400 to-sky-600" />

      <ToastDismissButton
        onClick={() => dispatch(removeToastNotification(toast.id))}
      />

      {/* Player piece avatar with a robot badge */}
      <div className="relative shrink-0">
        <div className="flex items-center justify-center h-20 w-20 rounded-full bg-black/50 ring-4 ring-sky-500/60 shadow-inner">
          {toast.pieceImgSrc ? (
            <img className="h-14 w-14 object-contain" src={toast.pieceImgSrc} />
          ) : (
            <i className="fa-solid fa-user text-white/50 text-3xl" />
          )}
        </div>
        <span className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-sky-500 ring-4 ring-[#0d1a29] flex items-center justify-center shadow-md">
          <i className="fa-solid fa-robot text-white text-base" />
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-sky-400">
          <i className="fa-solid fa-right-from-bracket" />
          Player left
        </div>
        <div className="text-2xl font-bold leading-tight mt-1">
          <span className="text-sky-300">{toast.username}</span>
          <span className="text-white">
            {" "}
            left — a bot is playing their seat
          </span>
        </div>
      </div>
    </div>
  )

  // The turn limit closing in. Sized to land like the trade toasts — this is
  // the moment the whole game changes shape, so it is not something to squint
  // at. It stays out of the way by living in the corner and taking itself away,
  // not by being small: nothing here has to be answered, and everything that
  // moves (the sheen, the final-turn glow, the draining rail) stays inside its
  // own border so nothing on the board twitches while a turn is being played.
  //
  // Under 1000px — the `mobile:` variant, the width that flips `isMobile` — the
  // clamp above is wider than some phones are, so the card takes a fixed, much
  // narrower width and every size is respelled in px: index.css halves the root
  // font-size below that width, so the rem sizes here would otherwise arrive at
  // half scale on exactly the screens with the least room to spare. Narrower
  // card, bigger type.
  //
  // The padding and the gaps are px for the same reason, not just the type:
  // `gap-3` and `mt-1` came out at 6px and 2px, which is close enough to
  // nothing that the label, the headline and the trophy line read as one
  // paragraph. The left padding is small because the dismiss gutter is empty on
  // touch — it only has to clear the accent rail, so the width it used to
  // reserve sits on the right edge instead.
  const renderEndgameWarningNotification = (
    toast: IEndgameWarningToastNotification
  ) => {
    const isFinalTurn = toast.turnsLeft <= 1
    const accent = isFinalTurn
      ? {
          border: "border-red-500/70",
          background: "from-[#4a1010] via-[#1d0808] to-black",
          rail: "from-red-400 to-red-600",
          ring: "ring-red-500/60",
          text: "text-red-300",
          label: "text-red-400",
          icon: "fa-flag-checkered",
        }
      : {
          border: "border-amber-400/60",
          background: "from-[#48330a] via-[#1d1405] to-black",
          rail: "from-amber-300 to-amber-500",
          ring: "ring-amber-400/60",
          text: "text-amber-200",
          label: "text-amber-400",
          icon: "fa-hourglass-half",
        }

    return (
      <div
        key={toast.id}
        data-testid="notification.endgame-warning"
        className={`endgame-warning-toast ${
          isFinalTurn ? "endgame-warning-toast--final" : ""
        } relative flex items-center gap-5 pl-16 pr-6 py-6 rounded-3xl w-[clamp(460px,34vw,640px)] overflow-hidden font-poppins text-white border-2 mobile:w-fit mobile:max-w-[340px] mobile:gap-[14px] mobile:rounded-[18px] mobile:pl-[16px] mobile:pr-[24px] mobile:py-[14px] ${
          accent.border
        } bg-gradient-to-br ${
          accent.background
        } shadow-[0_20px_50px_rgba(0,0,0,0.65)]`}
      >
        {/* Accent rail down the left edge, same as the trade toasts. */}
        <div
          className={`absolute inset-y-0 left-0 w-2 bg-gradient-to-b ${accent.rail}`}
        />

        {/* Light sweeping across the card, on a loop slow enough to read as
            atmosphere rather than as something asking to be clicked. */}
        <span className="endgame-warning-sheen pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        <ToastDismissButton
          onClick={() => dispatch(removeToastNotification(toast.id))}
        />

        {/* Turn counter — the number is the message, so it gets the size. */}
        <div
          className={`relative shrink-0 flex flex-col items-center justify-center h-24 w-24 rounded-3xl bg-black/50 ring-4 ${accent.ring} shadow-inner mobile:h-[54px] mobile:w-[54px] mobile:rounded-[14px]`}
        >
          <span className="text-5xl font-bold leading-none mobile:text-[28px]">
            {toast.turnsLeft}
          </span>
          <span className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1 mobile:mt-[3px] mobile:text-[8px] mobile:tracking-[0.15em]">
            {toast.turnsLeft === 1 ? "turn" : "turns"}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div
            className={`flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] mobile:gap-[6px] mobile:text-[9px] mobile:tracking-[0.12em] ${accent.label}`}
          >
            <i className={`fa-solid ${accent.icon}`} />
            {isFinalTurn ? "Final turn" : "Game ending soon"}
          </div>
          <div className="text-2xl font-bold leading-tight mt-1 mobile:mt-[5px] mobile:text-[15px]">
            {isFinalTurn ? (
              <span className={accent.text}>Last turn of the game</span>
            ) : (
              <>
                <span className={accent.text}>{toast.turnsLeft} turns</span>
                <span className="text-white"> left to play</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 text-base text-white/70 leading-tight mt-2 mobile:mt-[8px] mobile:gap-[6px] mobile:text-[10px]">
            <i className="fa-solid fa-trophy text-white/40" />
            Highest net worth wins
          </div>
        </div>

        {/* Drains over exactly as long as the toast is up, so the countdown to
            it leaving is visible instead of it just vanishing mid-glance. */}
        <span
          className={`endgame-warning-drain absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r ${accent.rail}`}
          style={{
            animationDuration: `${toastTypeToDuration["endgame-warning"]}ms`,
          }}
        />
      </div>
    )
  }

  // How a fast-pace vote ended. Green for a game that just got quicker, red for
  // one that stays as it was — and in the rejected case it says *why*, because
  // "somebody said no" and "somebody never looked at it" are different things
  // to the player deciding whether to propose it again.
  //
  // On a phone (`mobile:`, under the 1000px that flips `isMobile`) the card
  // sizes to its own text instead of holding the desktop clamp. The clamp is a
  // width the halved root font-size below 1000px cannot fill — "The game is now
  // fast paced" at 12px inside a 440px card is a line of type with a third of
  // the card empty beside it. `w-fit` plus a cap ends the empty right-hand
  // side, and the type goes up to use the room that is left.
  //
  // Spacing is spelled in px alongside it: the rem utilities halve under the
  // same media query, so the card's own padding, the gap to the icon tile and
  // the space between the three vote counts all collapsed to a few pixels
  // while the type stayed where it was put.
  const renderFastPaceResultNotification = (
    toast: IFastPaceResultToastNotification
  ) => {
    const accent = toast.accepted
      ? {
          border: "border-emerald-400/60",
          background: "from-[#0e2a1a] via-[#0a1f14] to-black",
          rail: "from-emerald-400 to-emerald-600",
          ring: "ring-emerald-500/60",
          label: "text-emerald-400",
          text: "text-emerald-300",
          icon: "fa-bolt",
        }
      : {
          border: "border-red-500/60",
          background: "from-[#4a1414] via-[#2a0e0e] to-black",
          rail: "from-red-400 to-red-600",
          ring: "ring-red-500/60",
          label: "text-red-400",
          text: "text-red-300",
          icon: "fa-ban",
        }
    const reason = toast.rejectedByName
      ? `${toast.rejectedByName} voted against it`
      : toast.timedOut
      ? "Not everyone answered in time"
      : "The table did not agree"
    return (
      <div
        key={toast.id}
        data-testid="notification.fast-pace-result"
        className={`toast-pop relative flex items-center gap-5 pl-16 pr-6 py-6 rounded-3xl w-[clamp(440px,32vw,620px)] overflow-hidden font-poppins text-white border-2 mobile:w-fit mobile:max-w-[340px] mobile:gap-[14px] mobile:rounded-[18px] mobile:pl-[16px] mobile:pr-[24px] mobile:py-[14px] ${accent.border} bg-gradient-to-br ${accent.background} shadow-[0_20px_50px_rgba(0,0,0,0.65)]`}
      >
        <div
          className={`absolute inset-y-0 left-0 w-2 bg-gradient-to-b ${accent.rail}`}
        />

        <ToastDismissButton
          onClick={() => dispatch(removeToastNotification(toast.id))}
        />

        <div
          className={`relative shrink-0 flex items-center justify-center h-16 w-16 rounded-2xl bg-black/50 ring-4 ${accent.ring} shadow-inner mobile:h-[44px] mobile:w-[44px] mobile:rounded-[12px]`}
        >
          <i
            className={`fa-solid ${accent.icon} ${accent.text} text-2xl mobile:text-[18px]`}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div
            className={`flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] mobile:gap-[6px] mobile:text-[8px] mobile:tracking-[0.12em] ${accent.label}`}
          >
            <i className="fa-solid fa-gavel" />
            {toast.accepted ? "Vote passed" : "Vote failed"}
          </div>
          <div className="text-2xl font-bold leading-tight mt-1 mobile:mt-[5px] mobile:text-2xl">
            {toast.accepted ? (
              <span className={accent.text}>The game is now fast paced</span>
            ) : (
              <>
                <span className="text-white">Pace stays as it is — </span>
                <span className={accent.text}>{reason}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-white/60 mt-2 mobile:mt-[8px] mobile:gap-[12px] mobile:text-[7px]">
            <span className="inline-flex items-center gap-1.5 mobile:gap-[4px]">
              <i className="fa-solid fa-thumbs-up text-emerald-400/70" />
              {toast.acceptedCount} accepted
            </span>
            <span className="inline-flex items-center gap-1.5 mobile:gap-[4px]">
              <i className="fa-solid fa-thumbs-down text-red-400/70" />
              {toast.rejectedCount} rejected
            </span>
            <span className="inline-flex items-center gap-1.5 mobile:gap-[4px]">
              <i className="fa-solid fa-users text-white/40" />
              {toast.acceptedCount + toast.rejectedCount}/{toast.totalVoters}{" "}
              voted
            </span>
          </div>
        </div>
      </div>
    )
  }

  const renderToast = (toast: IToastNotification) => {
    if (toast.type === "general") {
      return renderGeneralNotification(toast)
    } else if (toast.type === "game-invite") {
      return renderGameInviteNotification(toast)
    } else if (toast.type === "trade-declined") {
      return renderTradeDeclinedNotification(toast)
    } else if (toast.type === "trade-completed") {
      return renderTradeCompletedNotification(toast)
    } else if (toast.type === "player-replaced-by-bot") {
      return renderPlayerReplacedByBotNotification(toast)
    } else if (toast.type === "endgame-warning") {
      return renderEndgameWarningNotification(toast)
    } else if (toast.type === "fast-pace-result") {
      return renderFastPaceResultNotification(toast)
    }
    return null
  }

  return (
    // gap-4 is 16px on desktop but 8px on a phone, where the rem shrinks with
    // the root font-size — three of these cards stacked at 8px apart read as
    // one tall slab. Respelled in px there, like the cards' own padding.
    <div className="fixed top-6 right-6 z-[300] flex flex-col items-end gap-4 mobile:gap-[14px]">
      {toastNotifications.map((toast) => (
        // Swiping is how a toast is closed on touch — the dismiss button is
        // gone there. A completed trade keeps its flight into the trades panel
        // as its exit, so the swipe hands over instead of flinging the card.
        <SwipeableToast
          key={toast.id}
          enabled={isMobileDevice}
          customExit={toast.type === "trade-completed"}
          onDismiss={() =>
            toast.type === "trade-completed"
              ? minimizeTradeCompletedToast(toast)
              : dispatch(removeToastNotification(toast.id))
          }
        >
          {renderToast(toast)}
        </SwipeableToast>
      ))}
    </div>
  )
}

export default ToastContainer
